import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AddRounded,
  PersonAddAlt1Rounded,
  TuneRounded,
  CalendarMonthRounded,
  SchoolRounded,
  GroupRounded,
  CheckCircleOutlineRounded,
  FolderSpecialRounded,
  EditRounded,
  DeleteOutlineRounded,
  ChevronRightRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
} from '@mui/icons-material';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';
import type { TFunction } from 'i18next';
import { useEffect, useState, useMemo, useRef } from 'react';
import { sectionsTotalCapacity } from '@dosfilos/domain';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { CreateSectionDialog } from './components/CreateSectionDialog';
import { EditTermDialog } from './components/EditTermDialog';
import { DeleteTermDialog } from './components/DeleteTermDialog';
import { formatDate } from '../../lib/i18n-format';
import { CLOSED_PAGE_SIZE, isClosedTerm, termIdChunks } from './sections-terms';
import { sectionsHeaderActions, termCardActions } from './sections.actions';
import { useCanManageAcademics } from '../../hooks/use-can-manage-academics';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FACULTY_ROLES = [
  'faculty',
  'docente',
  'tutor',
  'decano',
  'academico',
  'coordinador',
  'capellan',
];

function fmtDate(val: any): string {
  if (!val) return '';
  let d: Date;
  if (val.toDate) {
    // Firestore Timestamp
    d = val.toDate();
    // Legacy data was stored as new Date('YYYY-MM-DD') which is UTC midnight.
    // In negative-offset timezones (Chile UTC-3) that displays as the previous day.
    // Detect UTC-midnight pattern and extract UTC date components instead.
    if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      d = new Date(`${y}-${m}-${day}T12:00:00`);
    }
  } else if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    // Pure date string (YYYY-MM-DD): append midday to avoid UTC-midnight off-by-one
    d = new Date(val + 'T12:00:00');
  } else {
    d = new Date(val);
  }
  if (isNaN(d.getTime())) return '';
  return formatDate(d, { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusColor(status: string): 'success' | 'error' | 'warning' | 'primary' | 'default' {
  if (status === 'open') return 'success';
  if (status === 'in_progress') return 'primary';
  if (status === 'cancelled') return 'error';
  if (status === 'planned') return 'warning';
  return 'default';
}

function statusLabel(status: string, t: TFunction): string {
  if (status === 'open') return t('sections.statusLabels.open');
  if (status === 'in_progress') return t('sections.statusLabels.in_progress');
  if (status === 'closed') return t('sections.statusLabels.closed');
  if (status === 'planned') return t('sections.statusLabels.planned');
  return status;
}

// ─── TermCard ─────────────────────────────────────────────────────────────────

interface TermCardProps {
  term: DocumentData;
  sections: DocumentData[];
  courses: DocumentData[];
  onEdit: () => void;
  onDelete: () => void;
  onConfigure: () => void;
  onNavigate: () => void;
  canManage: boolean;
}

function TermCard({
  term,
  sections,
  courses,
  onEdit,
  onDelete,
  onConfigure,
  onNavigate,
  canManage,
}: TermCardProps) {
  const { t } = useTranslation('sis');
  const termSections = useMemo(
    () => sections.filter((s) => s.termId === term.id && s.status !== 'cancelled'),
    [sections, term.id],
  );

  const cancelledSections = useMemo(
    () => sections.filter((s) => s.termId === term.id && s.status === 'cancelled'),
    [sections, term.id],
  );

  const uniqueFaculty = useMemo(
    () => new Set(termSections.map((s) => s.facultyId).filter(Boolean)),
    [termSections],
  );

  const totalEnrolled = useMemo(
    () => termSections.reduce((sum, s) => sum + (s.currentEnrollment ?? 0), 0),
    [termSections],
  );

  const totalCapacity = useMemo(
    // null = sin límite: basta UNA sección sin cupo para que el total no lo tenga.
    () => sectionsTotalCapacity(termSections.map((s) => s.maxEnrollment)),
    [termSections],
  );

  const uniquePrograms = useMemo(() => {
    const progIds = new Set(
      termSections.map((s) => courses.find((c) => c.id === s.courseId)?.programId).filter(Boolean),
    );
    return progIds.size;
  }, [termSections, courses]);

  // Deletable = terms no longer running (closed or planned); open/in_progress may not be deleted.
  const isDeletable = term.status === 'closed' || term.status === 'planned';
  const actions = termCardActions({ canManage, isDeletable });

  return (
    <Paper
      variant="outlined"
      onClick={onNavigate}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          boxShadow: '0 2px 20px 0 rgba(99,102,241,0.14)',
          borderColor: 'rgba(99,102,241,0.35)',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ px: 3, py: 2.5, gap: 2 }}
      >
        {/* Left: name + meta */}
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" mb={0.75}>
            <Typography variant="h6" fontWeight={700}>
              {term.name}
            </Typography>
            {term.status && (
              <Chip
                label={statusLabel(term.status, t)}
                size="small"
                color={statusColor(term.status)}
                sx={{ fontSize: 11, height: 22, fontWeight: 600 }}
              />
            )}
          </Stack>

          {/* Stats row */}
          <Stack direction="row" spacing={2.5} flexWrap="wrap">
            {(term.startDate || term.endDate) && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CalendarMonthRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                  {fmtDate(term.startDate) || '?'} – {fmtDate(term.endDate) || '?'}
                </Typography>
              </Stack>
            )}
            {uniquePrograms > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <FolderSpecialRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('sections.programCount', { count: uniquePrograms })}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <SchoolRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">
                {t('sections.subjectCount', { count: termSections.length })}
                {cancelledSections.length > 0 && (
                  <span style={{ opacity: 0.5 }}>
                    {' '}
                    {t('sections.cancelledCount', { count: cancelledSections.length })}
                  </span>
                )}
              </Typography>
            </Stack>
            {uniqueFaculty.size > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <GroupRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('sections.facultyCount', { count: uniqueFaculty.size })}
                </Typography>
              </Stack>
            )}
            {totalEnrolled > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CheckCircleOutlineRounded sx={{ fontSize: 14, color: 'primary.light' }} />
                <Typography variant="caption" color="primary.light">
                  {t('sections.enrolledCount', {
                    count: totalEnrolled,
                    enrolled: `${totalEnrolled}${totalCapacity !== null ? `/${totalCapacity}` : ''}`,
                  })}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Right: actions — stop click propagation so they don't trigger card navigation */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexShrink={0}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Edit period metadata */}
          {actions.includes('edit') && (
            <Tooltip title={t('sections.tooltips.editTerm')}>
              <IconButton
                size="small"
                onClick={onEdit}
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.light' } }}
              >
                <EditRounded sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Configure subjects */}
          {actions.includes('configure') && (
            <Tooltip title={t('sections.tooltips.configureSubjects')}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<TuneRounded sx={{ fontSize: 15 }} />}
                onClick={onConfigure}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, px: 1.5 }}
              >
                {t('sections.subjects')}
              </Button>
            </Tooltip>
          )}

          {/* Delete period */}
          {actions.includes('delete') && (
            <Tooltip title={t('sections.tooltips.deleteTerm')}>
              <IconButton
                size="small"
                onClick={onDelete}
                sx={{ color: 'text.disabled', '&:hover': { color: 'error.light' } }}
              >
                <DeleteOutlineRounded sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Navigate to detail */}
          <Tooltip title={t('sections.tooltips.viewDetail')}>
            <IconButton
              size="small"
              onClick={onNavigate}
              sx={{
                color: 'primary.light',
                bgcolor: 'rgba(99,102,241,0.08)',
                '&:hover': { bgcolor: 'rgba(99,102,241,0.18)' },
              }}
            >
              <ChevronRightRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function SectionsPage() {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const navigate = useNavigate();
  const canManage = useCanManageAcademics();

  // Secciones SOLO de los períodos a la vista, por lote (ver sections-terms.ts).
  const [sectionsByChunk, setSectionsByChunk] = useState<Record<string, DocumentData[]>>({});
  const [courses, setCourses] = useState<DocumentData[]>([]);
  const [terms, setTerms] = useState<DocumentData[]>([]);
  // Personas: solo para elegir docente al crear una sección, al abrir el diálogo.
  const [people, setPeople] = useState<DocumentData[] | null>(null);
  const [programs, setPrograms] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(false);
  // Períodos cerrados: plegados; al abrirlos, de a CLOSED_PAGE_SIZE.
  const [closedOpen, setClosedOpen] = useState(false);
  const [closedShown, setClosedShown] = useState(CLOSED_PAGE_SIZE);

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [editTerm, setEditTerm] = useState<DocumentData | null>(null);
  const [deleteTerm, setDeleteTerm] = useState<DocumentData | null>(null);

  const facultyPeople = useMemo(
    () =>
      (people ?? []).filter(
        (p) =>
          p.types?.some((t: string) => FACULTY_ROLES.includes(t)) ||
          p.institutionalRoles?.some((r: string) => FACULTY_ROLES.includes(r)),
      ),
    [people],
  );

  // Sort terms newest first
  const sortedTerms = useMemo(
    () =>
      [...terms].sort((a, b) => {
        const aDate = a.startDate?.toDate
          ? a.startDate.toDate()
          : a.startDate
            ? new Date(a.startDate)
            : new Date(0);
        const bDate = b.startDate?.toDate
          ? b.startDate.toDate()
          : b.startDate
            ? new Date(b.startDate)
            : new Date(0);
        return bDate.getTime() - aDate.getTime();
      }),
    [terms],
  );

  useEffect(() => {
    if (!currentTenantId) return;
    setLoading(true);
    setPeople(null);
    setClosedOpen(false);
    setClosedShown(CLOSED_PAGE_SIZE);
    const unsubCourses = onSnapshot(collection(db, `tenants/${currentTenantId}/courses`), (s) =>
      setCourses(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    const unsubTerms = onSnapshot(collection(db, `tenants/${currentTenantId}/terms`), (s) => {
      setTerms(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    const unsubPrograms = onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (s) =>
      setPrograms(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    return () => {
      unsubCourses();
      unsubTerms();
      unsubPrograms();
    };
  }, [currentTenantId]);

  const activeTerms = useMemo(() => sortedTerms.filter((tm) => !isClosedTerm(tm)), [sortedTerms]);
  const closedTerms = useMemo(() => sortedTerms.filter(isClosedTerm), [sortedTerms]);
  const shownClosedTerms = useMemo(
    () => (closedOpen ? closedTerms.slice(0, closedShown) : []),
    [closedOpen, closedTerms, closedShown],
  );

  // Una suscripción por lote de ids. Abrir otra página de cerrados agrega un
  // lote; los que ya estaban no se vuelven a suscribir ni a leer.
  const chunkKeys = useMemo(
    () =>
      termIdChunks(
        activeTerms.map((tm) => tm.id as string),
        shownClosedTerms.map((tm) => tm.id as string),
      ).map((c) => c.join(',')),
    [activeTerms, shownClosedTerms],
  );
  const subscriptions = useRef(new Map<string, () => void>());
  useEffect(() => {
    if (!currentTenantId) return;
    const subs = subscriptions.current;
    const wanted = new Set(chunkKeys);
    for (const [key, unsub] of subs) {
      if (!wanted.has(key)) {
        unsub();
        subs.delete(key);
        setSectionsByChunk((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    }
    for (const key of chunkKeys) {
      if (subs.has(key)) continue;
      subs.set(
        key,
        onSnapshot(
          query(
            collection(db, `tenants/${currentTenantId}/sections`),
            where('termId', 'in', key.split(',')),
          ),
          (snap) =>
            setSectionsByChunk((prev) => ({
              ...prev,
              [key]: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
            })),
        ),
      );
    }
  }, [currentTenantId, chunkKeys]);
  useEffect(() => {
    const subs = subscriptions.current;
    return () => {
      subs.forEach((u) => u());
      subs.clear();
      setSectionsByChunk({});
    };
  }, [currentTenantId]);
  const sections = useMemo(() => Object.values(sectionsByChunk).flat(), [sectionsByChunk]);

  useEffect(() => {
    if (!currentTenantId || !createOpen || people) return;
    getDocs(
      query(collection(db, `tenants/${currentTenantId}/people`), where('deletedAt', '==', null)),
    )
      .then((s) => setPeople(s.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(() => setPeople([]));
  }, [currentTenantId, createOpen, people]);

  const renderTermCard = (term: DocumentData) => (
    <TermCard
      key={term.id as string}
      term={term}
      sections={sections}
      courses={courses}
      onEdit={() => setEditTerm(term)}
      onDelete={() => setDeleteTerm(term)}
      onConfigure={() => navigate(`/sections/configure/${term.id as string}`)}
      onNavigate={() => navigate(`/sections/term/${term.id as string}`)}
      canManage={canManage}
    />
  );
  const headerActions = sectionsHeaderActions({ canManage });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* ── Page header ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="white">
            {t('sections.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('sections.termCount', { count: sortedTerms.length })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {headerActions.includes('importStudents') && (
            <Button
              variant="outlined"
              startIcon={<PersonAddAlt1Rounded />}
              onClick={() => navigate('/students/bulk-enroll')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('sections.importStudents')}
            </Button>
          )}
          {headerActions.includes('newTerm') && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setCreateOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {t('sections.new')}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* ── Term cards ── */}
      {loading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : sortedTerms.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 8, textAlign: 'center' }}>
          <Typography color="text.disabled">{t('sections.emptyState.title')}</Typography>
          <Typography variant="caption" color="text.disabled">
            {t('sections.emptyState.hint')}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {activeTerms.map(renderTermCard)}

          {/* Períodos cerrados: plegados, y al abrirlos de a CLOSED_PAGE_SIZE. */}
          {closedTerms.length > 0 && (
            <Button
              variant="text"
              color="inherit"
              onClick={() => setClosedOpen((v) => !v)}
              endIcon={closedOpen ? <ExpandLessRounded /> : <ExpandMoreRounded />}
              aria-expanded={closedOpen}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
            >
              {t('sections.closedTerms.title', { count: closedTerms.length })}
            </Button>
          )}
          {shownClosedTerms.map(renderTermCard)}
          {closedOpen && closedShown < closedTerms.length && (
            <Button
              variant="outlined"
              onClick={() => setClosedShown((n) => n + CLOSED_PAGE_SIZE)}
              sx={{ alignSelf: 'center', textTransform: 'none' }}
            >
              {t('sections.closedTerms.showMore', {
                count: Math.min(CLOSED_PAGE_SIZE, closedTerms.length - closedShown),
                remaining: closedTerms.length - closedShown,
              })}
            </Button>
          )}
        </Stack>
      )}

      {/* ── Dialogs ── */}

      <CreateSectionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        programs={programs.filter((p) => p.status === 'active')}
        courses={courses.filter((c) => c.status === 'active')}
        terms={terms.filter((t) => ['planned', 'open'].includes(t.status))}
        faculty={facultyPeople}
      />

      <EditTermDialog open={editTerm !== null} onClose={() => setEditTerm(null)} term={editTerm} />

      <DeleteTermDialog
        open={deleteTerm !== null}
        onClose={() => setDeleteTerm(null)}
        term={deleteTerm}
        sections={sections}
      />
    </Box>
  );
}
